import { z } from "zod";
import {
  CheckId,
  CheckResult,
  CheckSeverity,
  Decision,
  IsoDateTime,
  OperatorEnvironment,
  PlayerRef,
  PlayerSessionRef,
  Product,
  Purpose,
} from "./common.ts";
import { Reason } from "./reasons.ts";

/** Optional request header of `POST /v1/verify`: the product (mode) the SDK runs; must equal the token's product (403 `product_mismatch`). */
export const STRUST_MODE_HEADER = "X-STrust-Mode";

export const LocationState = z
  .enum(["granted", "permission_denied", "unavailable", "location_timeout", "not_requested"])
  .meta({ id: "LocationState" });

export const Coordinates = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy_m: z.number().nonnegative(),
    collected_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "Coordinates" });

export const LocationSignal = z
  .object({ state: LocationState, coords: Coordinates.optional() })
  .strict()
  .refine((l) => (l.state === "granted") === (l.coords !== undefined), {
    message: "coords are present exactly when state is granted",
    path: ["coords"],
  })
  .meta({ id: "LocationSignal" });

/** Documented device signals (spec browser-guard-sdk "Device signal collection"); no PII, no MAC/Wi-Fi. */
export const DeviceSignals = z
  .object({
    user_agent: z.string().max(512),
    browser: z.object({ name: z.string().max(64), version: z.string().max(64) }).strict(),
    os: z.object({ name: z.string().max(64), version: z.string().max(64).optional() }).strict(),
    platform: z.string().max(64),
    language: z.string().max(35),
    timezone: z.string().max(64),
    screen: z.object({ width: z.int().positive(), height: z.int().positive() }).strict(),
    features: z.record(z.string().max(64), z.boolean()),
    webdriver: z.boolean(),
  })
  .strict()
  .meta({ id: "DeviceSignals" });

const baseSignals = {
  nonce: z.string().min(16).max(128),
  collected_at: IsoDateTime,
  purpose: Purpose,
  sdk: z.object({ name: z.string().max(64), version: z.string().max(32) }).strict(),
  device: DeviceSignals,
  /**
   * SDK-generated UUID v4 per browser + origin; absent when storage is unavailable. An absent or
   * malformed value never refuses the request: it is dropped here and the verification completes
   * with the advisory reason `device_ref_missing` (spec risk-verification "Missing device reference").
   */
  device_ref: z.uuidv4().optional().catch(undefined).meta({
    description:
      "SDK-generated UUID v4 per browser and operator origin. Absent or malformed: the verification still completes and records device_ref_missing.",
  }),
  device_ref_reset: z.boolean().optional(),
};

export const PrecheckSignals = z
  .object({
    ...baseSignals,
    location: z
      .object({ state: z.literal("not_requested") })
      .strict()
      .optional(),
  })
  .strict()
  .meta({ id: "PrecheckSignals" });

export const BrowserGuardSignals = z
  .object({ ...baseSignals, location: LocationSignal })
  .strict()
  .meta({ id: "BrowserGuardSignals" });

export const PlcSignals = BrowserGuardSignals.meta({ id: "PlcSignals" });

export const GlvSignals = z
  .object({ ...baseSignals, location: LocationSignal })
  .strict()
  .refine((s) => s.purpose !== "other", {
    message: "glv does not accept purpose other",
    path: ["purpose"],
  })
  .refine((s) => s.location.state === "granted", {
    message: "glv requires a precise location",
    path: ["location"],
  })
  .meta({ id: "GlvSignals" });

/** Schema by product; the product comes from the session token, never from the body. */
export const SIGNALS_BY_PRODUCT = {
  precheck: PrecheckSignals,
  "browser-guard": BrowserGuardSignals,
  plc: PlcSignals,
  glv: GlvSignals,
} as const satisfies Record<Product, z.ZodType>;

export const VerifyRequest = z.union([PrecheckSignals, BrowserGuardSignals, GlvSignals]).meta({
  id: "VerifyRequest",
  description: "Validated against the schema of the token's product.",
});

export const CheckOutcome = z
  .object({
    id: CheckId,
    result: CheckResult,
    severity: CheckSeverity,
    required: z.boolean(),
    cause: z.string().optional(),
  })
  .meta({ id: "CheckOutcome" });

/**
 * `verification_id`: a time-ordered UUID v7 (RFC 9562) generated by S-Trust. Validated as any UUID
 * so clients treat it as opaque; its leading 48 bits are the creation time in Unix milliseconds.
 */
export const VerificationIdField = z
  .uuid()
  .describe(
    "Verification id: time-ordered UUID v7 (RFC 9562) generated by S-Trust; treat it as opaque.",
  );

export const VerifyResponse = z
  .object({
    verification_id: VerificationIdField,
    decision: Decision,
    reasons: z.array(Reason),
    checks: z.array(CheckOutcome),
    verdict_token: z.string(),
    valid_until: IsoDateTime.optional(),
    next_check_in: z.int().positive().optional(),
    buffer_time: z.int().nonnegative().optional(),
  })
  .refine((r) => (r.decision === "allow") === (r.next_check_in !== undefined), {
    message: "allow carries the revalidation window; deny has no next_check_in",
    path: ["next_check_in"],
  })
  .meta({ id: "VerifyResponse" });
export type VerifyResponse = z.infer<typeof VerifyResponse>;

/** Claims of the verdict token (ES256 JWT, typ strust-verdict+jwt), validated offline via JWKS. */
export const VerdictTokenClaims = z
  .object({
    iss: z.string(),
    aud: z.uuid().describe("operator_id"),
    iat: z.int(),
    exp: z.int(),
    verification_id: VerificationIdField,
    operator_id: z.uuid(),
    env: OperatorEnvironment,
    product: Product,
    player_session_ref: PlayerSessionRef,
    player_ref: PlayerRef,
    purpose: Purpose,
    decision: Decision,
    reasons: z.array(Reason),
    valid_until: IsoDateTime.optional(),
    next_check_in: z.int().positive().optional(),
    buffer_time: z.int().nonnegative().optional(),
  })
  .meta({ id: "VerdictTokenClaims" });
export type VerdictTokenClaims = z.infer<typeof VerdictTokenClaims>;

export const VerificationLookup = z
  .object({
    verification_id: VerificationIdField,
    product: Product,
    purpose: Purpose,
    decision: Decision,
    reasons: z.array(Reason),
    created_at: IsoDateTime,
  })
  .meta({ id: "VerificationLookup" });
